'use client'

import { AlertTriangle } from 'lucide-react'
import { AppError } from '@/lib/errors/appError'
import { getErrorCopy, type ErrorTier } from '@/lib/errors/messages'
import { ErrorActions } from '@/components/errors/error-actions'
import { cn } from '@/lib/utils'

interface ErrorFallbackProps {
  tier: ErrorTier
  error: unknown
  reset: () => void
  onBack: () => void
  title?: string
  description?: string
  compact?: boolean
}

export function ErrorFallback({
  tier,
  error,
  reset,
  onBack,
  title,
  description,
  compact = tier === 'component',
}: ErrorFallbackProps) {
  const copy = getErrorCopy(error, tier)
  const showDetails = process.env.NODE_ENV !== 'production'

  return (
    <div
      className={cn(
        'border-border bg-card flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center',
        compact ? 'min-h-32' : 'min-h-[50vh] gap-4 p-10',
      )}
      role="alert"
    >
      <AlertTriangle
        className={cn('text-destructive', compact ? 'size-6' : 'size-10')}
        aria-hidden
      />
      <div className="space-y-1">
        <p className={cn('text-foreground font-semibold', compact ? 'text-sm' : 'text-lg')}>
          {title ?? copy.title}
        </p>
        <p className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
          {description ?? copy.description}
        </p>
      </div>
      <ErrorActions onRetry={reset} onBack={onBack} />
      {error instanceof AppError && error.requestId ? (
        <p className="text-muted-foreground text-xs">Reference: {error.requestId}</p>
      ) : null}
      {showDetails && error instanceof Error ? (
        <details className="text-muted-foreground mt-2 w-full max-w-lg text-left text-xs">
          <summary className="cursor-pointer">Error details (dev only)</summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap">
            {error.message}
            {'\n'}
            {error.stack}
          </pre>
        </details>
      ) : null}
    </div>
  )
}
